import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useState, useRef, useEffect } from 'react';
import { Wrapper, Main, TopContentContainer, Heading, SingleItemContent, Footer } from './style.js';
import PopoutMenuItem from './PopoutMenuItem.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useIsMobile from '../../hooks/useIsMobile.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import DraggableDrawer from '../DraggableDrawer/DraggableDrawer.js';

var PopoutMenu = function PopoutMenu(_ref) {
  var _ref$activeItemId = _ref.activeItemId,
    parentActiveItemId = _ref$activeItemId === void 0 ? null : _ref$activeItemId,
    _ref$alignContentToIt = _ref.alignContentToItem,
    alignContentToItem = _ref$alignContentToIt === void 0 ? false : _ref$alignContentToIt,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? null : _ref$className,
    _ref$drawerHeight = _ref.drawerHeight,
    drawerHeight = _ref$drawerHeight === void 0 ? undefined : _ref$drawerHeight,
    _ref$footerContent = _ref.footerContent,
    footerContent = _ref$footerContent === void 0 ? undefined : _ref$footerContent,
    _ref$menuItems = _ref.menuItems,
    menuItems = _ref$menuItems === void 0 ? [] : _ref$menuItems,
    _ref$onActiveItemChan = _ref.onActiveItemChange,
    onActiveItemChange = _ref$onActiveItemChan === void 0 ? undefined : _ref$onActiveItemChan,
    onClose = _ref.onClose,
    _ref$show = _ref.show,
    show = _ref$show === void 0 ? false : _ref$show,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? 'Menu title' : _ref$title,
    _ref$topContent = _ref.topContent,
    topContent = _ref$topContent === void 0 ? null : _ref$topContent,
    _ref$triggerElements = _ref.triggerElements,
    triggerElements = _ref$triggerElements === void 0 ? [] : _ref$triggerElements;
  var _useState = useState(parentActiveItemId),
    _useState2 = _slicedToArray(_useState, 2),
    activeItemId = _useState2[0],
    setActiveItemId = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasOverflow = _useState4[0],
    setHasOverflow = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isScrolledDown = _useState6[0],
    setIsScrolledDown = _useState6[1];
  var wrapperRef = useRef(null);
  var mainRef = useRef(null);
  var isMobile = useIsMobile();
  var hasOpenItem = activeItemId;
  var checkActiveItemContent = function checkActiveItemContent() {
    if (!activeItemId) {
      return false;
    }
    var menuItem = menuItems.find(function (item) {
      return item.id === activeItemId;
    });
    return menuItem && menuItem.content !== undefined;
  };
  var activeItemHasContent = checkActiveItemContent();
  var handleToggleActiveItem = function handleToggleActiveItem(id) {
    setActiveItemId(id);
    if (onActiveItemChange) {
      onActiveItemChange(id);
    }
  };

  // On mobile, detect user scrolled to bottom (to hide footer shadow, if content overflow)
  useEffect(function () {
    var getIsScrollable = function getIsScrollable(element) {
      return element.scrollHeight > element.clientHeight;
    };
    if (mainRef.current && isMobile) {
      var container = mainRef.current;
      var handleScroll = function handleScroll() {
        var isScrollable = getIsScrollable(container);
        var isBottom = container.scrollHeight - Math.ceil(container.scrollTop) <= container.clientHeight;
        setIsScrolledDown(isScrollable ? isBottom : false);
      };
      container.addEventListener('scroll', handleScroll);
      return function () {
        container.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [isMobile]);
  useEffect(function () {
    if (mainRef.current && isMobile) {
      // On mobile, detect content overflow (for show/hide footer shadow)
      var resizeObserver = new ResizeObserver(function (entries) {
        if (!entries.length) return;
        var _entries$0$target = entries[0].target,
          scrollHeight = _entries$0$target.scrollHeight,
          clientHeight = _entries$0$target.clientHeight;
        if (scrollHeight > clientHeight) {
          setHasOverflow(true);
        } else {
          setHasOverflow(false);
        }
      });

      // On mobile, make sure that user is scrolled to top when submenu is shown
      if (activeItemId && isMobile) {
        var scrollToTop = function scrollToTop() {
          if (mainRef.current) {
            mainRef.current.scrollTo({
              top: 0
            });
          }
        };
        scrollToTop();
      }
      resizeObserver.observe(mainRef.current);
      return function () {
        resizeObserver.disconnect();
      };
    }
    return undefined;
  }, [isMobile, activeItemId]);
  var prevShow = useRef(show);
  useEffect(function () {
    var shouldRunCloseEffects = prevShow.current && !show;
    prevShow.current = show;
    if (!shouldRunCloseEffects) return;
    if (!parentActiveItemId) setActiveItemId(null);
    onClose === null || onClose === void 0 || onClose();
  }, [show, parentActiveItemId, onClose]);
  var enableClickOutSide = show && !isMobile;
  useOnClickOutside(wrapperRef, onClose, enableClickOutSide, false, triggerElements);
  if (!(menuItems !== null && menuItems !== void 0 && menuItems.length)) {
    return null;
  }
  return /*#__PURE__*/jsx(DraggableDrawer, {
    show: show,
    disabled: !isMobile,
    onClose: onClose,
    defaultHeight: drawerHeight,
    noContentPadding: true,
    children: /*#__PURE__*/jsxs(Wrapper, {
      ref: wrapperRef,
      className: className
      // @ts-ignore
      ,
      $show: show,
      children: [/*#__PURE__*/jsxs(Main, {
        ref: mainRef,
        children: [topContent && !hasOpenItem && /*#__PURE__*/jsxs(TopContentContainer, {
          children: [/*#__PURE__*/jsx(Heading, {
            // @ts-ignore
            $hide: activeItemHasContent,
            children: topContent.title
          }), topContent.content]
        }), /*#__PURE__*/jsx(Heading, {
          // @ts-ignore
          $hide: hasOpenItem && activeItemHasContent,
          children: title
        }), menuItems.length > 1 ? /*#__PURE__*/jsx("ul", {
          children: menuItems.map(function (item) {
            var hiddenOnMobile = activeItemId && activeItemId !== item.id && activeItemHasContent;
            return /*#__PURE__*/jsx(PopoutMenuItem, {
              id: item.id,
              title: item.title,
              activeItemId: activeItemId,
              hiddenOnMobile: hiddenOnMobile,
              onToggle: handleToggleActiveItem,
              filterItems: item.filterItems,
              alignContentToItem: alignContentToItem,
              children: item.content
            }, item.id);
          })
        }) : /*#__PURE__*/jsx(SingleItemContent, {
          children: menuItems[0].content
        })]
      }), footerContent && /*#__PURE__*/jsx(Footer, {
        // @ts-ignore
        $overflow: hasOverflow && !isScrolledDown,
        children: footerContent
      })]
    })
  });
};
var PopoutMenu$1 = PopoutMenu;

export { PopoutMenu$1 as default };
//# sourceMappingURL=PopoutMenu.js.map
